import React from 'react'
import { Input, Radio, Select } from 'antd'
import { FormikHelpers, FormikState } from 'formik'
import clsx from 'clsx'
import {
  AdminsFinanceCreateVendorRequest,
  AdminsFinanceGetBankListResponseItem,
  TaxTypeEnum,
} from 'generated/api'
import { regexNumber } from 'constants/regex'
import Card from 'components/card/Card'
import CardHeaderForm from 'components/card/CardHeaderForm'
import Upload from 'components/form/Upload'
import ErrorHintForm from 'components/errors/ErrorHintForm'
import { classNameInput } from 'helpers/formik/classNameForm'
import dropdownScrollFetch from 'helpers/dropdownScrollFetch'

interface LegalityInformationProps {
  state: FormikState<AdminsFinanceCreateVendorRequest>
  helpers: FormikHelpers<AdminsFinanceCreateVendorRequest>
  banks: AdminsFinanceGetBankListResponseItem[]
  onChangePageBanks: (page: number) => void
  totalPageBanks: number
  pageBanks: number
}

export default function FormLegalityInformation(props: LegalityInformationProps) {
  const { state, helpers, banks, onChangePageBanks, totalPageBanks, pageBanks } = props

  const handlePopupScroll = (event: React.UIEvent<HTMLDivElement>) => {
    dropdownScrollFetch(event, pageBanks, totalPageBanks, () => onChangePageBanks(pageBanks + 1))
  }

  const classNameInputTaxIdentityNo = classNameInput('taxIdentityNo', state)
  const classNameInputBankAccountNo = classNameInput('bankAccountNo', state)
  const classNameInputBankAccountName = classNameInput('bankAccountName', state)

  return (
    <Card className="rounded-xl py-6 px-0">
      <CardHeaderForm title="3. Legalitas Vendor" />
      <Card.Body className="mt-0 px-6">
        <div className="flex flex-col gap-3 xl:gap-9">
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Jenis NPWP</label>
            </div>
            <div className="xl:w-9/12">
              <Radio.Group
                name="taxType"
                className="flex gap-6"
                value={state.values.taxType}
                onChange={({ target: { value } }) => {
                  helpers.setFieldValue('taxType', value)
                }}
              >
                <Radio
                  className={`xl:w-4/12 py-4 px-2 mr-0 rounded-lg border-solid border-2 border-[#E5E5E7]
                    ${clsx({
                      'bg-[#ECEEEB] text-[#3C5337]': state.values.taxType === TaxTypeEnum.Personal,
                      'bg-[#fff] text-[#323539]': state.values.taxType !== TaxTypeEnum.Personal,
                    })}
                  `}
                  value={TaxTypeEnum.Personal}
                >
                  <span className="font-medium text-base my-0 antialiased">Pribadi</span>
                </Radio>
                <Radio
                  className={`xl:w-4/12 py-4 px-2 mr-0 rounded-lg border-solid border-2 border-[#E5E5E7]
                    ${clsx({
                      'bg-[#ECEEEB] text-[#3C5337]':
                        state.values.taxType === TaxTypeEnum.Organization,
                      'bg-[#fff] text-[#323539]': state.values.taxType !== TaxTypeEnum.Organization,
                    })}
                  `}
                  value={TaxTypeEnum.Organization}
                >
                  <span className="font-medium text-base my-0 antialiased">Perusahaan</span>
                </Radio>
              </Radio.Group>
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Nomor NPWP</label>
            </div>
            <div className="xl:w-9/12">
              <Input
                id="taxIdentityNo"
                name="taxIdentityNo"
                className={`xl:w-2/5 mb-1 w-full ${classNameInputTaxIdentityNo}`}
                status={state.errors.taxIdentityNo && state.touched.taxIdentityNo ? 'error' : ''}
                placeholder="Masukkan Nomor NPWP"
                value={state.values.taxIdentityNo ? state.values.taxIdentityNo : ''}
                onChange={({ target: { value } }) => {
                  if (regexNumber.test(value)) {
                    helpers.setFieldValue('taxIdentityNo', value)
                  }
                }}
              />
              {state.errors.taxIdentityNo && state.touched.taxIdentityNo ? (
                <ErrorHintForm
                  errorMessage={state.errors.taxIdentityNo}
                  dataTestId="error-message-taxIdentityNo"
                />
              ) : null}
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Nama Bank</label>
            </div>
            <div className="xl:w-9/12">
              <div>
                <Select
                  id="bankId"
                  className="selector mb-1 h-full xl:w-2/5 w-full"
                  placeholder="Pilih Bank"
                  virtual={false}
                  filterOption={false}
                  showSearch={false}
                  disabled={false}
                  value={state.values.bankId ? state.values.bankId : undefined}
                  status={state.errors.bankId && state.touched.bankId ? 'error' : ''}
                  onChange={value => helpers.setFieldValue('bankId', value)}
                  onPopupScroll={handlePopupScroll}
                >
                  {banks.map(product => {
                    return (
                      <Select.Option key={product.id} value={product.id}>
                        {product.name}
                      </Select.Option>
                    )
                  })}
                </Select>
              </div>
              {state.errors.bankId && state.touched.bankId ? (
                <ErrorHintForm
                  errorMessage={state.errors.bankId}
                  dataTestId="error-message-bankId"
                />
              ) : null}
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">
                Nomor Rekening
              </label>
            </div>
            <div className="xl:w-9/12">
              <Input
                id="bankAccountNo"
                name="bankAccountNo"
                className={`xl:w-2/5 mb-1 w-full ${classNameInputBankAccountNo}`}
                status={state.errors.bankAccountNo && state.touched.bankAccountNo ? 'error' : ''}
                placeholder="Masukkan Nomor Rekening"
                value={state.values.bankAccountNo ? state.values.bankAccountNo : ''}
                onChange={({ target: { value } }) => {
                  if (regexNumber.test(value)) {
                    helpers.setFieldValue('bankAccountNo', value)
                  }
                }}
              />
              {state.errors.bankAccountNo && state.touched.bankAccountNo ? (
                <ErrorHintForm
                  errorMessage={state.errors.bankAccountNo}
                  dataTestId="error-message-bankAccountNo"
                />
              ) : null}
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">
                Nama Pemilik Rekening
              </label>
            </div>
            <div className="xl:w-9/12">
              <Input
                id="bankAccountName"
                name="bankAccountName"
                className={`xl:w-2/5 mb-1 w-full ${classNameInputBankAccountName}`}
                placeholder="Masukkan Nama Pemilik Rekening"
                value={state.values.bankAccountName}
                onChange={e => {
                  helpers.setFieldTouched('bankAccountName', true)
                  helpers.setFieldValue('bankAccountName', e.target.value)
                }}
                status={
                  state.errors.bankAccountName && state.touched.bankAccountName ? 'error' : ''
                }
              />
              {state.errors.bankAccountName && state.touched.bankAccountName ? (
                <ErrorHintForm
                  errorMessage={state.errors.bankAccountName}
                  dataTestId="error-message-bankAccountName"
                />
              ) : null}
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">
                Upload Buku Tabungan / Surat Pernyataan
              </label>
            </div>
            <div className="xl:w-9/12">
              <Upload
                labelButton="Pilih File"
                placeholder="Masukkan File Buku Tabungan..."
                accept="image/jpg"
                helpers={helpers}
                pathFileName="vendor"
                value={state.values.savingBookFilePath ? state.values.savingBookFilePath : ''}
                formKey="savingBookFilePath"
                operationType="VendorSavingBook"
              />
            </div>
          </div>
          <div className="xl:flex xl:gap-6">
            <div className="xl:w-3/12 self-center">
              <label className="text-base font-medium text-[#323539] m-0 block">Upload PKS</label>
            </div>
            <div className="xl:w-9/12">
              <Upload
                labelButton="Pilih File"
                placeholder="Masukkan File PKS...."
                accept="application/pdf"
                helpers={helpers}
                pathFileName="vendor"
                value={state.values.mouFilePath ? state.values.mouFilePath : ''}
                formKey="mouFilePath"
                operationType="VendorMoU"
              />
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
